import { Injectable, computed, inject, signal } from '@angular/core';
import { CatalogProduct } from '../models/catalog.model';
import { CheckoutService } from './checkout.service';

export interface CartLine {
  id: string;
  slug: string;
  name: string;
  subtitle: string;
  unitPrice: number;
  imageUrl: string;
  quantity: number;
}

const STORAGE_KEY = 'ecommerce.cart.scent.v4';
const DEFAULT_TAX_RATE = 0.07;

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly checkoutService = inject(CheckoutService);
  private readonly lines = signal<CartLine[]>(this.readStorage());

  readonly taxRate = signal(DEFAULT_TAX_RATE);
  readonly taxRatePercent = computed(() => Math.round(this.taxRate() * 100));

  readonly items = this.lines.asReadonly();
  readonly itemCount = computed(() => this.lines().reduce((sum, line) => sum + line.quantity, 0));

  readonly subtotal = computed(() =>
    this.lines().reduce((sum, line) => sum + line.unitPrice * line.quantity, 0)
  );
  readonly tax = computed(() =>
    Math.round(this.subtotal() * this.taxRate() * 100) / 100
  );
  readonly total = computed(() => this.subtotal() + this.tax());

  constructor() {
    this.checkoutService.getConfig().subscribe({
      next: (config) => {
        if (config.taxRate >= 0) {
          this.taxRate.set(config.taxRate);
        }
      }
    });
  }

  add(product: CatalogProduct, quantity = 1, variantId?: string): void {
    const qty = Math.max(1, Math.floor(quantity));
    const variants = (product.variants ?? [])
      .filter((v) => v.isActive)
      .sort((a, b) => a.position - b.position);
    const variant =
      (variantId ? variants.find((v) => v.id === variantId) : null) ?? variants[0] ?? null;
    const lineId = variant?.id ?? product.defaultVariantId ?? product.id;
    const optionLabel =
      variant?.optionValueId == null
        ? null
        : (product.options ?? [])
            .flatMap((o) => o.values)
            .find((v) => v.id === variant.optionValueId)?.value ?? null;
    const unitPrice = variant?.price ?? product.price;
    const name = optionLabel ? `${product.name} - ${optionLabel}` : product.name;

    this.lines.update((current) => {
      const index = current.findIndex((line) => line.id === lineId);
      if (index < 0) {
        return [
          ...current,
          {
            id: lineId,
            slug: product.slug,
            name,
            subtitle: product.subtitle,
            unitPrice,
            imageUrl: product.imageUrl,
            quantity: qty
          }
        ];
      }

      return current.map((line, i) =>
        i === index ? { ...line, quantity: line.quantity + qty, unitPrice, name } : line
      );
    });
    this.persist();
  }

  setQuantity(id: string, quantity: number): void {
    const qty = Math.floor(quantity);
    if (qty < 1) {
      this.remove(id);
      return;
    }

    this.lines.update((current) =>
      current.map((line) => (line.id === id ? { ...line, quantity: qty } : line))
    );
    this.persist();
  }

  increment(id: string): void {
    const line = this.lines().find((item) => item.id === id);
    if (line) {
      this.setQuantity(id, line.quantity + 1);
    }
  }

  decrement(id: string): void {
    const line = this.lines().find((item) => item.id === id);
    if (line) {
      this.setQuantity(id, line.quantity - 1);
    }
  }

  remove(id: string): void {
    this.lines.update((current) => current.filter((line) => line.id !== id));
    this.persist();
  }

  clear(): void {
    this.lines.set([]);
    this.persist();
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.lines()));
    } catch {
      // Ignore quota / private mode failures.
    }
  }

  private readStorage(): CartLine[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as CartLine[];
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .filter((line) => line?.id && line.quantity > 0)
        .map((line) => ({
          id: String(line.id),
          slug: String(line.slug ?? ''),
          name: String(line.name ?? ''),
          subtitle: String(line.subtitle ?? ''),
          unitPrice: Number(line.unitPrice) || 0,
          imageUrl: String(line.imageUrl ?? ''),
          quantity: Math.max(1, Math.floor(Number(line.quantity) || 1))
        }));
    } catch {
      return [];
    }
  }
}
